'use client';

import { MapPinIcon } from 'lucide-react';
import { Slot } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '../lib/utils';

export interface LocationsFilterButtonProps extends ComponentPropsWithoutRef<'button'> {
  asChild?: boolean;
  /** Label next to the icon (e.g. "All locations"). */
  children?: ReactNode;
  /** Replaces the map pin, so other sidebar filters can share this chrome. */
  icon?: ReactNode;
}

/**
 * Expanded Aspen sidebar locations filter — Figma `locations-filter` (7565:18580).
 * Presentational only; wire Popover / selection in the app.
 */
export const LocationsFilterButton = forwardRef<HTMLButtonElement, LocationsFilterButtonProps>(
  ({ asChild = false, className, type = 'button', children, icon, ...props }, ref) => {
    const Comp = asChild ? Slot.Root : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        data-slot="locations-filter-button"
        data-testid="location-selector-trigger"
        className={cn(
          'flex h-9 w-full min-w-0 appearance-none items-center justify-center gap-2 rounded-lg border border-border-muted bg-background-surface px-4',
          'text-interaction-neutral-content-default label-sm',
          'outline-hidden transition-colors hover:bg-background-muted',
          'focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          'disabled:pointer-events-none disabled:opacity-50',
          '[&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:stroke-current',
          className,
        )}
        {...props}
      >
        {icon ?? <MapPinIcon aria-hidden />}
        <span className="min-w-0 truncate">{children}</span>
      </Comp>
    );
  },
);
LocationsFilterButton.displayName = 'LocationsFilterButton';

export interface LocationsFilterIconButtonProps extends ComponentPropsWithoutRef<'button'> {
  asChild?: boolean;
  /** True while the locations popover is open — matches `NavSidebarActionButton`. */
  isActive?: boolean;
}

/**
 * Collapsed Aspen rail map-pin control — matches `NavSidebarActionButton` chrome.
 */
export const LocationsFilterIconButton = forwardRef<
  HTMLButtonElement,
  LocationsFilterIconButtonProps
>(({ asChild = false, className, type = 'button', isActive = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      data-slot="locations-filter-icon-button"
      data-testid="location-selector-trigger"
      data-active={isActive || undefined}
      aria-pressed={isActive || undefined}
      className={cn(
        'relative flex items-center justify-center rounded-sm p-1 text-foreground-secondary transition-colors',
        'outline-hidden hover:bg-background-elevated/60 focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-current',
        isActive && 'bg-background-elevated text-foreground-accent [&_svg]:text-foreground-accent',
        className,
      )}
      {...props}
    >
      {children ?? <MapPinIcon aria-hidden />}
    </Comp>
  );
});
LocationsFilterIconButton.displayName = 'LocationsFilterIconButton';
